import React from "react";
import styled from "@emotion/styled";
import { smoothTransition } from "styles/animations";
import { Flex } from "styles/utility-components";

type Props = {
  width?: number;
  height?: number;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  fullWidth?: boolean;
  backgroundColor?: string;
  href?: string;
  padding?: number;
};

function Card({ children, onClick, href, ...rest }: Props) {
  const onClickHandler = onClick
    ? (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        onClick(e);
        if (!href) {
          e.preventDefault();
        }
        e.stopPropagation();
      }
    : undefined;
  return (
    <CardContainer
      as={href ? "a" : "div"}
      onClick={onClickHandler}
      tabIndex={0}
      href={href}
      {...rest}
    >
      {children}
    </CardContainer>
  );
}

export default Card;

const CardContainer = styled(Flex)<Omit<Props, "children">>(
  ({ theme, width, height, fullWidth, onClick, backgroundColor, padding }) => ({
    width: fullWidth ? "100%" : width,
    height,
    backgroundColor: backgroundColor ?? theme.colors.sysBackgroundDefault,
    flexDirection: "column",
    cursor: onClick ? "pointer" : "auto",
    borderRadius: 4,
    border: `1px solid ${theme.colors.sysBorderDecorative}`,
    padding: padding ?? 16,
    gap: 8,
    ...(onClick && {
      "&:hover": {
        backgroundColor: theme.colors.sysPrimarySubtle,
        borderColor: theme.colors.sysPrimaryHover,
        transition: smoothTransition,
      },
      "&:focus": {
        borderColor: theme.colors.sysPrimaryPressed,
        backgroundColor: theme.colors.sysPrimarySubtleAlt,
      },
    }),
  }),
);
